import { ChangeEvent, useState } from 'react';

import { GearbeltEvent } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { Countries, TimezoneMapping } from '@site-mate/sitemate-global-shared';
import dayjs from 'dayjs';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { UseFormReturn } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormFieldItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { PhoneInput } from '@/components/ui/phone-input';
import { env } from '@/environment';
import { SignUpSteps, useSignUpStore } from '@/stores/signup.store';
import { useEvents } from '@/utils/events';

import { IPasswordValidation, PasswordValidationsList } from './password-validations';
import { SignUpFormData, SignUpStepProps, validatePassword } from './signup-form-utils';

export function EmailAndUserInfoStep({ form, loading, onNextStep, register, hasEmailError }: SignUpStepProps<SignUpFormData>) {
  const { signUpStep } = useSignUpStore();
  const [focusedFields, setFocusedFields] = useState<{ [key: string]: boolean }>({});
  const [passwordValidation, setPasswordValidation] = useState<IPasswordValidation | null>(null);

  const {
    control,
    formState: { errors },
  } = form;

  const isGetStartedButtonDisabled = (form: UseFormReturn<SignUpFormData>) => {
    const formHasErrors = Object.keys(form.formState.errors).length > 0;
    const { email, firstName, lastName, workspaceName, password, isSSOSignUp } = form.getValues();
    const formHasEmptyFields = !email.length || !firstName.length || !lastName.length || !workspaceName.length;
    const isInvalid = formHasErrors || formHasEmptyFields || (!isSSOSignUp && password.length <= 1);
    return isInvalid;
  };

  const handleFocus = (field: string) => {
    setFocusedFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field: string) => {
    setFocusedFields((prev) => ({ ...prev, [field]: false }));
    form.trigger(field as keyof SignUpFormData);
  };

  function onPasswordChange(event: ChangeEvent<HTMLInputElement>): void {
    if (!event.target.value) {
      setPasswordValidation(null);
      return;
    }
    setPasswordValidation(validatePassword(event.target.value));
  }

  return (
    <>
      <div className="flex flex-col items-center gap-3">
        <h2 className="text-lg font-[500]">Create your free Gearbelt account</h2>
        <div className="text-secondary-text text-sm">No credit card required</div>
      </div>
      <Form {...form}>
        <form className="flex flex-col gap-2">
          <div className="flex flex-col gap-4">
            <FormFieldItem control={control} name="email" noErrorMessage={!hasEmailError}>
              {({ value, error }) => (
                <Input
                  placeholder="Email"
                  value={value as string}
                  error={!!error && hasEmailError}
                  {...register('email')}
                  disabled={signUpStep === SignUpSteps.UserInfo}
                  onFocus={() => handleFocus('email')}
                  onBlur={() => handleBlur('email')}
                  className={signUpStep === SignUpSteps.UserInfo ? 'bg-grey-2 cursor-not-allowed' : ''}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && onNextStep) {
                      e.preventDefault();
                      onNextStep();
                      e.currentTarget.blur();
                    }
                  }}
                />
              )}
            </FormFieldItem>

            {signUpStep === SignUpSteps.Email && (
              <Button
                className="w-full py-3 text-base"
                type="button"
                loading={loading}
                onClick={onNextStep}
                disabled={!form.getValues().email || (errors.email && hasEmailError)}>
                Next
              </Button>
            )}
          </div>

          {signUpStep === SignUpSteps.UserInfo && (
            <div className="animate-in slide-in-from-top-3 flex flex-col gap-2 duration-500">
              <div className="flex-space-between flex gap-2">
                <FormFieldItem control={control} name="firstName" noErrorMessage={focusedFields.firstName || !form.getValues().firstName}>
                  {({ value, error }) => (
                    <Input
                      placeholder="First name"
                      value={value as string}
                      error={!!error && !focusedFields.firstName && !!value}
                      {...register('firstName')}
                      onFocus={() => handleFocus('firstName')}
                      onBlur={() => handleBlur('firstName')}
                    />
                  )}
                </FormFieldItem>

                <FormFieldItem control={control} name="lastName" noErrorMessage={focusedFields.lastName || !form.getValues().lastName}>
                  {({ value, error }) => (
                    <Input
                      placeholder="Last name"
                      value={value as string}
                      error={!!error && !focusedFields.lastName && !!value}
                      {...register('lastName')}
                      onFocus={() => handleFocus('lastName')}
                      onBlur={() => handleBlur('lastName')}
                    />
                  )}
                </FormFieldItem>
              </div>

              <FormFieldItem control={control} name="workspaceName" noErrorMessage={focusedFields.workspaceName || !form.getValues().workspaceName}>
                {({ value, error }) => (
                  <Input
                    placeholder="Company name"
                    value={value as string}
                    error={!!error && !focusedFields.workspaceName && !!value}
                    {...register('workspaceName')}
                    onFocus={() => handleFocus('workspaceName')}
                    onBlur={() => handleBlur('workspaceName')}
                  />
                )}
              </FormFieldItem>

              {form.getValues().isSSOSignUp ? (
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip>
                      <p className="m-auto text-left">
                        Looks like your email domain is already in use and has been verified, so you won’t need a password to login - instead you’ll be able to
                        login using your company’s SSO service.
                      </p>
                    </Tooltip>
                  }>
                  <div>
                    <FormFieldItem control={control} name="password" noErrorMessage={true}>
                      {({ value }) => {
                        return <Input placeholder="Password" value={value as string} disabled={true} className="bg-grey-2 cursor-not-allowed" />;
                      }}
                    </FormFieldItem>
                  </div>
                </OverlayTrigger>
              ) : (
                <FormFieldItem control={control} name="password" noErrorMessage={true}>
                  {({ value, onChange }) => {
                    const { onChange: registerOnChange } = register('password');
                    return (
                      <PasswordInput
                        placeholder="Password"
                        value={value as string}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          onPasswordChange(e);
                          registerOnChange(e);
                          onChange(e.target.value);
                        }}
                        onFocus={() => handleFocus('password')}
                        onBlur={() => handleBlur('password')}
                        disabled={form.getValues().isSSOSignUp}
                      />
                    );
                  }}
                </FormFieldItem>
              )}

              {passwordValidation && <PasswordValidationsList {...passwordValidation} />}

              <Button
                size="lg"
                className="mt-3 w-full py-3 text-base"
                type="button"
                loading={loading}
                onClick={onNextStep}
                disabled={isGetStartedButtonDisabled(form)}>
                Get Started
              </Button>
            </div>
          )}
        </form>
      </Form>
    </>
  );
}

export function MobileNumberStep({ form, loading, register, onNextStep }: SignUpStepProps<SignUpFormData>) {
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState<boolean>(false);
  const defaultCountry = 'au';
  const defaultContinent = 'OC';
  const userCountry = TimezoneMapping[dayjs.tz.guess()]?.countries[0]?.code?.toLowerCase();
  const continentByTimezone = Countries.find((c) => c.ccode.toLowerCase() === userCountry)?.continentCode;
  const [selectedCountryContinent, setSelectedCountryContinent] = useState<string>(continentByTimezone ?? defaultContinent);
  const { trackEvent } = useEvents();
  const {
    control,
    formState: { dirtyFields, errors },
  } = form;

  const europeContinentCode = 'EU';
  const urlMap = {
    [europeContinentCode]: {
      privacyPolicy: 'https://sitemate.com/uk/resources/privacy-policy',
      termsOfService: 'https://sitemate.com/uk/resources/terms-of-service',
    },
    default: {
      privacyPolicy: 'https://sitemate.com/resources/privacy-policy',
      termsOfService: 'https://sitemate.com/resources/terms-of-service',
    },
  };

  const handleSelectedCountry = (selectedCountry: string) => {
    const continent = Countries.find((c) => c.ccode.toLowerCase() === selectedCountry)?.continentCode;
    setSelectedCountryContinent(continent || 'OC');
  };

  function onCreateAccountClick() {
    trackEvent(GearbeltEvent.CTAClicked, {
      Context: 'Sign Up Page Phone Number',
    });

    const continent = continentByTimezone ?? selectedCountryContinent;
    form.setValue('continent', continent);
    onNextStep!();
  }

  return (
    <div className="z-10 m-auto flex min-h-[277px] w-[400px] flex-col gap-4 rounded bg-white p-8 shadow">
      <h2 className="text-left text-xl font-semibold">Add your phone number</h2>
      <p className="text-default-text text-sm">Your information will be kept private and secure, it won't be shared with any third parties.</p>
      <Form {...form}>
        <form className="space-between flex flex-col gap-2">
          <FormFieldItem control={control} name="mobile">
            {({ value, onChange, error }) => {
              const { onChange: registerOnChange } = register('mobile');
              return (
                <PhoneInput
                  placeholder="Phone number..."
                  value={value as string}
                  error={!!error}
                  onChange={(value) => {
                    onChange(value);
                    registerOnChange({ target: { value } });
                  }}
                  onCountryChange={handleSelectedCountry}
                  userCountry={userCountry}
                />
              );
            }}
          </FormFieldItem>
          <label className="flex items-center gap-1 pt-1 text-sm">
            <Checkbox onCheckedChange={() => setTermsAndConditionsAccepted(!termsAndConditionsAccepted)} />
            <span>
              I agree to the
              <a
                className="text-default-blue px-1 underline"
                href={selectedCountryContinent === 'EU' ? urlMap[selectedCountryContinent].privacyPolicy : urlMap.default.privacyPolicy}
                target="_blank"
                rel="noopener">
                Privacy Policy
              </a>
              &
              <a
                className="text-default-blue pl-1 underline"
                href={selectedCountryContinent === 'EU' ? urlMap[selectedCountryContinent].termsOfService : urlMap.default.termsOfService}
                target="_blank"
                rel="noopener">
                Terms of Service
              </a>
            </span>
          </label>
          <Button
            size="lg"
            className="mt-3 w-full py-3 text-base"
            type="button"
            loading={loading}
            onClick={onCreateAccountClick}
            disabled={!termsAndConditionsAccepted || !!errors.mobile || !dirtyFields.mobile}>
            Create account
          </Button>
        </form>
      </Form>
      <p className="text-secondary-text text-center text-xs">
        This site is protected by reCAPTCHA and the Google
        <a className="text-default-blue px-0.5 hover:underline" href="https://policies.google.com/privacy" target="_blank" rel="noopener">
          Privacy Policy
        </a>
        and
        <a className="text-default-blue px-0.5 hover:underline" href="https://policies.google.com/terms" target="_blank" rel="noopener">
          Terms of Service
        </a>
        apply.
      </p>
    </div>
  );
}

export function HumanVerificationStep({ form, loading, setReCAPTCHAV2Token, onNextStep }: SignUpStepProps<SignUpFormData>) {
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState<boolean>(false);
  const { trackEvent } = useEvents();

  const handleOnVerificationChange = (token: string | null) => {
    setIsRecaptchaVerified(true);
    if (setReCAPTCHAV2Token) {
      setReCAPTCHAV2Token(token);
    }
  };

  function onFinishClick() {
    trackEvent(GearbeltEvent.CTAClicked, {
      Context: 'Sign Up Page Human Verification',
    });

    onNextStep?.();
  }

  return (
    <div className="z-10 m-auto flex min-h-[277px] w-[400px] flex-col gap-4 rounded bg-white p-8 shadow">
      <h2 className="text-xl font-semibold">Are you human?</h2>
      <p className="text-default-text text-sm">Please complete the challenge below.</p>

      <Form {...form}>
        <form className="space-between flex flex-col gap-4">
          <ReCAPTCHA sitekey={env.recaptcha.v2SiteKey} onChange={handleOnVerificationChange} size="normal" />

          <Button size="lg" className="w-full py-2" type="button" loading={loading} onClick={onFinishClick} disabled={!isRecaptchaVerified}>
            Finish
          </Button>
        </form>
      </Form>
    </div>
  );
}
